import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TableColumn,
  TableEmptyState,
  TableInspector,
  TablePageAction,
  TablePageActionEvent,
  TablePageComponent,
  TableRowAction,
  TableRowActionEvent,
  TableSummaryItem
} from 'src/app/con-x/table/table-page.component';
import { UserService } from 'src/app/services/user-service/userservice';
import { AddressBookConfirmDialogComponent } from './address-book-confirm-dialog.component';
import { AddressBook } from './address-book.models';
import { AddressBookNameDialogComponent, addressBookErrorMessage } from './address-book-name-dialog.component';
import { AddressBookService } from './address-book.service';

@Component({
  selector: 'app-address-book-overview',
  standalone: true,
  imports: [TablePageComponent, MatSnackBarModule],
  templateUrl: './address-book-overview.component.html',
  styleUrls: ['./address-book-overview.component.scss']
})
export class AddressBookOverviewComponent implements OnInit {
  addressBooks: AddressBook[] = [];
  selectedAddressBook: AddressBook | null = null;
  loading = false;
  errorMessage = '';
  organisationId?: number;
  canManage = true;

  readonly columns: readonly TableColumn<AddressBook>[] = [
    { key: 'name', label: 'Adressbuch', secondaryValue: row => this.ownerLabel(row) },
    { key: 'entryCount', label: 'Kontakte', type: 'number', align: 'end' },
    { key: 'updatedAt', label: 'Zuletzt geändert', type: 'datetime' }
  ];

  readonly emptyState: TableEmptyState = {
    icon: 'menu_book',
    title: 'Noch kein Adressbuch vorhanden',
    description: 'Legen Sie ein Adressbuch an, um Kontakte strukturiert zu verwalten.',
    actionId: 'create'
  };

  constructor(
    private readonly addressBookService: AddressBookService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  get pageActions(): TablePageAction[] {
    return this.canManage ? [{ id: 'create', label: 'Adressbuch anlegen', icon: 'add' }] : [];
  }

  get rowActions(): readonly TableRowAction<AddressBook>[] {
    return [
      { id: 'open', label: 'Kontakte öffnen', icon: 'contacts' },
      { id: 'rename', label: 'Umbenennen', icon: 'edit', hidden: () => !this.canManage },
      { id: 'delete', label: 'Adressbuch löschen', icon: 'delete', tone: 'warn', hidden: () => !this.canManage }
    ];
  }

  get inspector(): TableInspector<AddressBook> {
    return {
      ariaLabel: 'Details des ausgewählten Adressbuchs',
      title: 'name',
      subtitle: row => this.ownerLabel(row),
      description: row => `${row.entryCount} ${row.entryCount === 1 ? 'Kontakt' : 'Kontakte'}`,
      icon: 'menu_book',
      fields: [
        { label: 'Erstellt', value: 'createdAt', type: 'datetime' },
        { label: 'Geändert', value: 'updatedAt', type: 'datetime' }
      ],
      actions: this.rowActions
    };
  }

  get summaryItems(): readonly TableSummaryItem<AddressBook>[] {
    return [
      { label: 'Adressbücher', value: this.addressBooks.length, icon: 'menu_book' },
      { label: 'Kontakte', value: this.addressBooks.reduce((sum, book) => sum + book.entryCount, 0), icon: 'contacts' }
    ];
  }

  ngOnInit(): void {
    const rawOrganisationId = this.route.parent?.snapshot.paramMap.get('organisationId');
    const parsedOrganisationId = rawOrganisationId ? Number(rawOrganisationId) : undefined;
    this.organisationId = parsedOrganisationId && !Number.isNaN(parsedOrganisationId) ? parsedOrganisationId : undefined;
    this.canManage = !this.organisationId
      || this.userService.isCurrentUserPlatformAdmin()
      || this.userService.isUserAdminOfOrganisationOrPlattformAdmin(this.organisationId);
    this.loadAddressBooks();
  }

  loadAddressBooks(): void {
    this.loading = true;
    this.errorMessage = '';
    this.addressBookService.getAddressBooks({ organisationId: this.organisationId }).subscribe({
      next: addressBooks => {
        this.addressBooks = addressBooks;
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      },
      error: error => {
        this.loading = false;
        this.errorMessage = this.errorText(error, 'Die Adressbücher konnten nicht geladen werden.');
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  handlePageAction(event: TablePageActionEvent): void {
    if (event.actionId === 'create') this.openNameDialog();
  }

  handleRowAction(event: TableRowActionEvent<AddressBook>): void {
    if (event.actionId === 'open') void this.router.navigate([event.row.addressBookId], { relativeTo: this.route });
    if (event.actionId === 'rename') this.openNameDialog(event.row);
    if (event.actionId === 'delete') this.confirmDelete(event.row);
  }

  private openNameDialog(addressBook?: AddressBook): void {
    this.dialog.open(AddressBookNameDialogComponent, {
      width: '31rem',
      maxWidth: 'calc(100vw - 2rem)',
      data: { organisationId: this.organisationId, addressBook }
    }).afterClosed().subscribe(result => {
      if (!result) return;
      this.snackBar.open(addressBook ? 'Adressbuch wurde umbenannt.' : 'Adressbuch wurde angelegt.', 'Schließen',
        { duration: 4000 });
      this.loadAddressBooks();
    });
  }

  private confirmDelete(addressBook: AddressBook): void {
    this.dialog.open(AddressBookConfirmDialogComponent, {
      width: '31rem',
      maxWidth: 'calc(100vw - 2rem)',
      data: {
        title: 'Adressbuch löschen',
        message: 'Alle enthaltenen Kontakte werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
        itemName: addressBook.name,
        confirmLabel: 'Adressbuch löschen'
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.addressBookService.deleteAddressBook(addressBook.addressBookId).subscribe({
        next: () => {
          this.selectedAddressBook = null;
          this.addressBooks = this.addressBooks.filter(book => book.addressBookId !== addressBook.addressBookId);
          this.snackBar.open('Adressbuch wurde gelöscht.', 'Schließen', { duration: 4000 });
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          this.snackBar.open(this.errorText(error, 'Das Adressbuch konnte nicht gelöscht werden.'),
            'Schließen', { duration: 6000 });
          this.changeDetectorRef.markForCheck();
        }
      });
    });
  }

  private ownerLabel(addressBook: AddressBook): string {
    return addressBook.ownerType === 'ORGANISATION' ? 'Organisation' : 'Persönlich';
  }

  private errorText(error: any, fallback: string): string {
    return addressBookErrorMessage(error, fallback);
  }
}
